import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RESTAURANT } from "@/lib/content";
import { useSiteContent } from "@/lib/site-content";

const LINKS = [
  { href: "#menu", label: "Menu" },
  { href: "#about", label: "About" },
  { href: "#gallery", label: "Gallery" },
  { href: "#reviews", label: "Reviews" },
  { href: "#reserve", label: "Reserve" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { content } = useSiteContent();
  const phone = content.contact.phone || RESTAURANT.phone;
  const phoneHref = `tel:${phone.replace(/\s/g, "")}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF8F5]/85 backdrop-blur-md border-b border-[#E8DED5]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="flex h-20 items-center justify-between">
          <a href="#top" data-testid="brand-logo" className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl overflow-hidden bg-[#F5ECE1] border border-[#E8DED5] shadow-[0_8px_22px_-12px_rgba(26,26,26,0.35)]">
              <img
                src={RESTAURANT.logo}
                alt="Thai Noodle & Rice logo"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="leading-tight">
              <div className="font-display font-black text-[15px] tracking-tight">
                Thai Noodle & Rice
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#5C534D]">
                Chinatown · MCR
              </div>
            </div>
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium text-[#1A1A1A] hover:text-[#E53935] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-[#E53935] hover:after:w-full after:transition-all"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={phoneHref}
              data-testid="navbar-phone"
              className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A] hover:text-[#E53935]"
            >
              <Phone className="h-4 w-4" />
              {phone}
            </a>
            <Button
              asChild
              data-testid="navbar-reserve-btn"
              className="rounded-full bg-[#E53935] hover:bg-[#C62828] text-white px-5 h-11 font-semibold"
            >
              <a href="#reserve">Book a table</a>
            </Button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            className="md:hidden rounded-full h-11 w-11 grid place-items-center border border-[#E8DED5] bg-white"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[#E8DED5] bg-[#FAF8F5]"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="text-base font-medium py-2"
              >
                {l.label}
              </a>
            ))}
            <a
              href={phoneHref}
              className="text-sm font-semibold text-[#E53935] pt-2 border-t border-[#E8DED5]"
            >
              Call {phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
